import React, { useEffect, useState, useCallback } from "react";
import api, { API, formatApiError } from "@/lib/api";
import { todayISO } from "@/lib/holiday";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Loader2, FileSpreadsheet, Printer, Download, CheckCircle2,
  AlertCircle, MinusCircle, XCircle, CalendarOff
} from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
  hadir: "bg-emerald-100 text-emerald-800",
  izin: "bg-sky-100 text-sky-800",
  sakit: "bg-amber-100 text-amber-800",
  alfa: "bg-red-100 text-red-800",
};
const STATUS_LABEL = { hadir: "Hadir", izin: "Izin", sakit: "Sakit", alfa: "Alfa" };

function StatCard({ icon: Icon, label, value, accent, testid }) {
  return (
    <Card data-testid={testid} className="p-4 border border-slate-200 shadow-sm bg-white">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{label}</div>
          <div className="mt-1 font-heading text-2xl font-bold text-slate-900 tabular-nums">{value}</div>
        </div>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${accent}`}>
          <Icon className="h-4 w-4" />
        </div>
      </div>
    </Card>
  );
}

function firstDayOfMonth() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${yyyy}-${mm}-01`;
}

export default function RekapAbsensiPage() {
  const [target, setTarget] = useState("santri");
  const [tanggalMulai, setTanggalMulai] = useState(firstDayOfMonth());
  const [tanggalAkhir, setTanggalAkhir] = useState(todayISO());
  const [kelasId, setKelasId] = useState("all");
  const [santriId, setSantriId] = useState("all");
  const [ustadzId, setUstadzId] = useState("all");
  const [kelasList, setKelasList] = useState([]);
  const [santriList, setSantriList] = useState([]);
  const [ustadzList, setUstadzList] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadFilters = useCallback(async () => {
    try {
      const [k, s, u] = await Promise.all([
        api.get("/kelas", { params: { limit: 200 } }),
        api.get("/santri", { params: { limit: 500 } }),
        api.get("/ustadz", { params: { limit: 200 } }),
      ]);
      setKelasList(k.data.items || []);
      setSantriList(s.data.items || []);
      setUstadzList(u.data.items || []);
    } catch (_e) { /* ignore */ }
  }, []);

  useEffect(() => { loadFilters(); }, [loadFilters]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { tanggal_mulai: tanggalMulai, tanggal_akhir: tanggalAkhir, target };
      if (target === "santri") {
        if (kelasId !== "all") params.kelas_id = kelasId;
        if (santriId !== "all") params.santri_id = santriId;
      } else {
        if (ustadzId !== "all") params.ustadz_id = ustadzId;
      }
      const { data: d } = await api.get("/absensi/rekap", { params });
      setData(d);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [target, tanggalMulai, tanggalAkhir, kelasId, santriId, ustadzId]);

  useEffect(() => { load(); }, [load]);

  const handleExportExcel = () => {
    const params = new URLSearchParams({
      tanggal_mulai: tanggalMulai, tanggal_akhir: tanggalAkhir, target,
    });
    if (target === "santri") {
      if (kelasId !== "all") params.set("kelas_id", kelasId);
      if (santriId !== "all") params.set("santri_id", santriId);
    } else if (ustadzId !== "all") {
      params.set("ustadz_id", ustadzId);
    }
    // Cookies are httpOnly+sameSite=none — direct navigation includes them.
    window.open(`${API}/absensi/rekap/export?${params.toString()}`, "_blank");
  };

  const handlePrint = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const today = new Date().toLocaleDateString("id-ID");
    const targetTitle = target === "santri" ? "Santri" : "Ustadz";
    const rows = (data.rows || []).map((r) => `
      <tr>
        <td>${r.tanggal || "-"}</td>
        ${target === "santri" ? `<td>${r.nis || "-"}</td><td>${r.santri_nama || "-"}</td><td>${r.kelas_nama || "-"}</td>` : `<td>${r.ustadz_nama || "-"}</td><td>${r.mata_pelajaran || "-"}</td>`}
        <td><b>${STATUS_LABEL[r.status] || r.status}</b></td>
        <td>${r.keterangan || ""}</td>
      </tr>`).join("");
    w.document.write(`
      <!doctype html><html><head><meta charset="utf-8" />
      <title>Rekap Absensi ${targetTitle}</title>
      <style>
        @page { size: A4; margin: 16mm; }
        body { font-family: 'Manrope', system-ui, sans-serif; color: #0f172a; padding: 0; margin: 0; font-size: 12px; }
        h1 { font-family: 'Outfit', sans-serif; font-size: 18px; color: #047857; margin: 0; }
        .sub { color: #475569; margin-top: 2px; font-size: 11px; }
        .hr { border-top: 1px solid #cbd5e1; margin: 10px 0; }
        .stats { display: flex; gap: 12px; margin-top: 8px; }
        .stat { flex: 1; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; }
        .stat .l { font-size: 9px; color: #64748b; text-transform: uppercase; letter-spacing: 1px; }
        .stat .v { font-size: 16px; font-weight: 700; }
        table { width: 100%; border-collapse: collapse; margin-top: 12px; }
        th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; font-size: 11px; }
        th { background: #f1f5f9; }
        .ft { margin-top: 16px; font-size: 10px; color: #64748b; text-align: center; }
      </style></head><body>
        <h1>SiMadin — Rekap Absensi ${targetTitle}</h1>
        <div class="sub">Madrasah Al Islamiyyah Sunan Giri Surabaya</div>
        <div class="sub">Periode: ${tanggalMulai} s/d ${tanggalAkhir} • Dicetak: ${today}</div>
        <div class="hr"></div>
        <div class="stats">
          <div class="stat"><div class="l">Hadir</div><div class="v">${data.counts.hadir}</div></div>
          <div class="stat"><div class="l">Izin</div><div class="v">${data.counts.izin}</div></div>
          <div class="stat"><div class="l">Sakit</div><div class="v">${data.counts.sakit}</div></div>
          <div class="stat"><div class="l">Alfa</div><div class="v">${data.counts.alfa}</div></div>
          <div class="stat"><div class="l">% Hadir</div><div class="v">${data.persen_hadir}%</div></div>
        </div>
        <div class="sub" style="margin-top:6px;">Hari efektif belajar pada periode ini: ${data.school_days} hari. ${data.holiday_note}</div>
        <table>
          <thead><tr>
            <th>Tanggal</th>
            ${target === "santri" ? "<th>NIS</th><th>Nama Santri</th><th>Kelas</th>" : "<th>Nama Ustadz</th><th>Mata Pelajaran</th>"}
            <th>Status</th>
            <th>Keterangan</th>
          </tr></thead>
          <tbody>${rows || `<tr><td colspan="6" style="text-align:center;color:#94a3b8;padding:16px;">Tidak ada data.</td></tr>`}</tbody>
        </table>
        <div class="ft">SiMadin © 2026 — Sistem Informasi Madrasah Al Islamiyyah Sunan Giri</div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>
    `);
    w.document.close();
  };

  return (
    <div className="space-y-6" data-testid="rekap-absensi-page">
      <div className="flex flex-col gap-1">
        <div className="text-xs font-semibold text-emerald-700 uppercase tracking-widest">Absensi</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 tracking-tight">Rekap Absensi</h1>
        <p className="text-slate-500 text-sm">Rekap kehadiran berdasarkan periode tanggal. Hari Selasa & Kamis tidak dihitung sebagai hari efektif.</p>
      </div>

      <Tabs value={target} onValueChange={setTarget}>
        <TabsList className="bg-white border border-slate-200 p-1">
          <TabsTrigger value="santri" data-testid="rekap-tab-santri">Rekap Santri</TabsTrigger>
          <TabsTrigger value="ustadz" data-testid="rekap-tab-ustadz">Rekap Ustadz</TabsTrigger>
        </TabsList>

        <TabsContent value={target}>
          <Card className="p-4 md:p-5 border border-slate-200 shadow-sm bg-white mt-2">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
              <div>
                <Label className="text-xs">Tanggal Mulai</Label>
                <Input type="date" value={tanggalMulai} onChange={(e) => setTanggalMulai(e.target.value)}
                       className="mt-1 h-10" data-testid="rekap-tanggal-mulai" />
              </div>
              <div>
                <Label className="text-xs">Tanggal Akhir</Label>
                <Input type="date" value={tanggalAkhir} onChange={(e) => setTanggalAkhir(e.target.value)}
                       className="mt-1 h-10" data-testid="rekap-tanggal-akhir" />
              </div>
              {target === "santri" ? (
                <>
                  <div>
                    <Label className="text-xs">Kelas</Label>
                    <Select value={kelasId} onValueChange={setKelasId}>
                      <SelectTrigger className="mt-1 h-10" data-testid="rekap-filter-kelas"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Kelas</SelectItem>
                        {kelasList.map((k) => <SelectItem key={k.id} value={k.id}>{k.nama}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Santri</Label>
                    <Select value={santriId} onValueChange={setSantriId}>
                      <SelectTrigger className="mt-1 h-10" data-testid="rekap-filter-santri"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Semua Santri</SelectItem>
                        {santriList.filter((s) => kelasId === "all" || s.kelas_id === kelasId).map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.nama} • {s.nis}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              ) : (
                <div className="md:col-span-2">
                  <Label className="text-xs">Ustadz</Label>
                  <Select value={ustadzId} onValueChange={setUstadzId}>
                    <SelectTrigger className="mt-1 h-10" data-testid="rekap-filter-ustadz"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Ustadz</SelectItem>
                      {ustadzList.map((u) => <SelectItem key={u.id} value={u.id}>{u.nama}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="flex items-end gap-2">
                <Button onClick={handleExportExcel} variant="outline" data-testid="rekap-export-excel" className="h-10 flex-1 gap-2">
                  <Download className="h-4 w-4" /> Excel
                </Button>
                <Button onClick={handlePrint} variant="outline" data-testid="rekap-print-pdf" className="h-10 flex-1 gap-2">
                  <Printer className="h-4 w-4" /> PDF
                </Button>
              </div>
            </div>
          </Card>

          {/* Summary */}
          {loading ? (
            <div className="py-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin text-slate-400" /></div>
          ) : data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-4">
                <StatCard testid="rekap-stat-hadir" icon={CheckCircle2} label="Hadir" value={data.counts.hadir} accent="bg-emerald-50 text-emerald-700" />
                <StatCard testid="rekap-stat-izin" icon={MinusCircle} label="Izin" value={data.counts.izin} accent="bg-sky-50 text-sky-700" />
                <StatCard testid="rekap-stat-sakit" icon={AlertCircle} label="Sakit" value={data.counts.sakit} accent="bg-amber-50 text-amber-700" />
                <StatCard testid="rekap-stat-alfa" icon={XCircle} label="Alfa" value={data.counts.alfa} accent="bg-red-50 text-red-700" />
                <StatCard testid="rekap-stat-persen" icon={FileSpreadsheet} label="% Hadir" value={`${data.persen_hadir}%`} accent="bg-emerald-50 text-emerald-700" />
              </div>
              <div className="text-xs text-slate-500 mt-2 flex items-center gap-2">
                <CalendarOff className="h-3.5 w-3.5" />
                Hari efektif: <b className="text-slate-700">{data.school_days}</b> hari. {data.holiday_note}
              </div>

              <Card className="border border-slate-200 shadow-sm bg-white overflow-hidden mt-4">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 hover:bg-slate-50">
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Tanggal</TableHead>
                        {target === "santri" ? (
                          <>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Santri</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Kelas</TableHead>
                          </>
                        ) : (
                          <>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Ustadz</TableHead>
                            <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Mata Pelajaran</TableHead>
                          </>
                        )}
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Status</TableHead>
                        <TableHead className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Keterangan</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {(!data.rows || data.rows.length === 0) ? (
                        <TableRow><TableCell colSpan={5} className="text-center py-10 text-slate-400">Tidak ada data pada periode ini.</TableCell></TableRow>
                      ) : data.rows.map((r) => (
                        <TableRow key={r.id} data-testid={`rekap-row-${r.id}`} className="hover:bg-emerald-50/30">
                          <TableCell className="text-sm text-slate-700 font-mono text-xs">{r.tanggal}</TableCell>
                          {target === "santri" ? (
                            <>
                              <TableCell>
                                <div className="font-semibold text-slate-900">{r.santri_nama}</div>
                                <div className="text-xs text-slate-500 font-mono">{r.nis}</div>
                              </TableCell>
                              <TableCell className="text-sm text-slate-700">{r.kelas_nama}</TableCell>
                            </>
                          ) : (
                            <>
                              <TableCell className="font-semibold text-slate-900">{r.ustadz_nama}</TableCell>
                              <TableCell className="text-sm text-slate-700">{r.mata_pelajaran || "-"}</TableCell>
                            </>
                          )}
                          <TableCell>
                            <span className={`px-2.5 py-0.5 inline-flex text-xs font-semibold rounded-full ${STATUS_BADGE[r.status] || "bg-slate-100"}`}>
                              {STATUS_LABEL[r.status] || r.status}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm text-slate-700">{r.keterangan || "-"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </Card>
            </>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
